import React from 'react';
import { motion } from 'framer-motion';
import { Laptop, CheckCircle, XCircle, Building } from 'lucide-react';
import { LivingInAreaData } from '../../types/livingInArea';

interface RemoteWorkSectionProps {
  data: LivingInAreaData['remoteWork'];
}

export const RemoteWorkSection: React.FC<RemoteWorkSectionProps> = ({ data }) => {
  return (
    <section>
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Stats Card */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="rounded-3xl border border-slate-200 bg-white p-10 text-slate-900 shadow-lg"
        >
          <div className="mb-8 flex items-center gap-3">
            <div className="rounded-2xl bg-indigo-50 p-3 text-indigo-600">
              <Laptop size={28} />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Remote Work</h2>
          </div>

          {/* Percentage */}
          <div className="mb-8">
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              className="mb-2 text-6xl font-black text-slate-900 md:text-7xl"
            >
              {data.percentage}%
            </motion.div>

            <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${data.percentage}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
                className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-500"
              />
            </div>

            <p className="mt-2 text-sm font-bold uppercase tracking-wider text-slate-600">
              WFH Adoption Rate
            </p>
          </div>

          {/* Common Status */}
          <div className="flex items-center gap-3 rounded-2xl bg-slate-100 p-4">
            {data.common ? (
              <CheckCircle size={24} className="text-emerald-600" />
            ) : (
              <XCircle size={24} className="text-rose-600" />
            )}

            <span className="font-semibold text-slate-800">
              Remote work is {data.common ? 'common' : 'less common'} here
            </span>
          </div>
        </motion.div>

        {/* Details Card */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="relative overflow-hidden rounded-3xl bg-slate-900 p-10 text-slate-100"
        >
          <motion.div
            className="absolute -bottom-6 -right-6 opacity-5"
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
          >
            <Building size={150} />
          </motion.div>

          <div className="relative z-10 flex h-full flex-col justify-between">
            <div>
              <h3 className="mb-4 text-lg font-bold opacity-70">Daytime Activity</h3>
              <p className="text-xl leading-relaxed">{data.daytimeActivity}</p>
            </div>

            {data.workspaceSuitability && (
              <div className="mt-8 border-t border-white/20 pt-6">
                <h3 className="mb-2 text-sm font-bold uppercase tracking-wider opacity-70">
                  Workspace Suitability
                </h3>
                <p className="text-lg font-medium">{data.workspaceSuitability}</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
